import React, { useCallback, useRef } from 'react';
import './ScheduleView.css';
import { Emblem } from '../../components/Emblem/Emblem';
import { WeekProgress } from '../../components/WeekProgress/WeekProgress';
import { DayCard } from './DayCard';
import { getCurrentDayIndex, getWeekKey } from './scheduleData';
import type { DaySchedule } from '../../types';

interface ScheduleViewProps {
  tasks: Record<string, boolean>;
  onToggleTask: (id: string) => void;
  onReset: () => void;
  schedule: DaySchedule[];
  activeDays: boolean[];
  householdName?: string;
  username?: string;
}

export const ScheduleView: React.FC<ScheduleViewProps> = ({
  tasks, onToggleTask, onReset, schedule, activeDays, householdName, username,
}) => {
  const currentDayIndex = getCurrentDayIndex();
  const scrollRef = useRef<HTMLDivElement>(null);

  const activeSchedule = schedule.filter((_, i) => activeDays[i]);
  const totalTasks = activeSchedule.reduce((sum, day) => sum + day.tasks.length, 0);
  const completedTasks = activeSchedule.reduce(
    (sum, day) => sum + day.tasks.filter((t) => tasks[t.id]).length, 0
  );

  const handleTodayRef = useCallback((el: HTMLDivElement | null) => {
    if (el) {
      setTimeout(() => { el.scrollIntoView({ behavior: 'smooth', block: 'center' }); }, 600);
    }
  }, []);

  const handleReset = () => {
    onReset();
  };

  const allComplete = completedTasks === totalTasks && totalTasks > 0;

  return (
    <div className="schedule-view" ref={scrollRef}>
      <header className="schedule-header">
        <div className="schedule-header-left">
          <span className="schedule-header-icon">🏠</span>
        </div>
        <div className="schedule-header-center">
          <h1 className="schedule-header-title">Weekly Schedule</h1>
          {username && <p className="schedule-header-welcome">Welcome, {username}</p>}
        </div>
        <button className="schedule-header-reset" onClick={handleReset} aria-label="Reset week" title="Reset all tasks">
          <svg viewBox="0 0 24 24" width="20" height="20">
            <path d="M17.65 6.35A7.95 7.95 0 0012 4C7.58 4 4.01 7.58 4.01 12S7.58 20 12 20c3.73 0 6.84-2.55 7.73-6h-2.08A5.99 5.99 0 0112 18c-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z" fill="currentColor" />
          </svg>
        </button>
      </header>

      <div className="schedule-content">
        <Emblem completedTasks={completedTasks} totalTasks={totalTasks} householdName={householdName} />

        <WeekProgress
          completedTasks={completedTasks}
          totalTasks={totalTasks}
          tasks={tasks}
          schedule={schedule}
          activeDays={activeDays}
        />

        <div className="schedule-grid">
          {schedule.map((day, index) => {
            if (!activeDays[index]) return null;
            return (
              <DayCard
                key={day.day}
                schedule={day}
                tasks={tasks}
                onToggleTask={onToggleTask}
                isToday={index === currentDayIndex}
                index={index}
                onTodayRef={index === currentDayIndex ? handleTodayRef : undefined}
              />
            );
          })}
        </div>

        {allComplete && (
          <div className="schedule-celebration">
            <span className="celebration-emoji">🎊</span>
            <h3 className="celebration-title">Week Complete!</h3>
            <p className="celebration-text">Amazing job! Your home is sparkling clean!</p>
          </div>
        )}

        <footer className="schedule-footer">
          <div className="schedule-footer-week">
            <svg viewBox="0 0 20 20" width="14" height="14" className="schedule-footer-icon">
              <rect x="3" y="4" width="14" height="13" rx="2" fill="none" stroke="currentColor" strokeWidth="1.5" />
              <line x1="3" y1="8" x2="17" y2="8" stroke="currentColor" strokeWidth="1.5" />
              <line x1="7" y1="2" x2="7" y2="5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              <line x1="13" y1="2" x2="13" y2="5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
            <span>{getWeekKey()}</span>
          </div>
          <p className="schedule-footer-tagline">Consistency is the key to a clean home ✨</p>
        </footer>
      </div>
    </div>
  );
};
